import { OrniTransportError } from "./transport.ts";
import { randomUuid } from "./ids.ts";
import { CloudflareNativeTerminalRelay } from "./cloudflare-native-terminal-relay.ts";
import type { NativeRelayClientDescriptor } from "./remote-terminal-ticket-client.ts";
import type {
  CloudflareNativeTerminalMultiplexRelay,
  NativeTerminalMultiplexStream,
} from "./cloudflare-native-terminal-multiplex-relay.ts";
import type { RemoteTerminalControllerState } from "./remote-terminal-observer.ts";

export interface RemoteTerminalControllerTarget {
  session_id: string;
  process_attempt_id: string;
  generation: number;
}

export interface RemoteTerminalLeaseProof {
  lease_id: string;
  lease_epoch: number;
  owner_attachment_id: string;
  target: RemoteTerminalControllerTarget;
}

export interface RemoteTerminalControllerLease extends RemoteTerminalLeaseProof {
  status: "offered" | "active" | "revoked" | "expired" | "superseded";
  renewed_at: string;
  expires_at: string;
  revision: number;
}

export interface RemoteTerminalControllerOptions {
  cloudUrl: string;
  deviceId: string;
  target: RemoteTerminalControllerTarget;
  /** Legacy Cloud control ticket. Fixed-device relay attachment does not use it. */
  ticket?: string;
  attachmentId: string;
  /** Optional Cloudflare relay for PTY input/resize/upload. Lease authority remains Cloud. */
  relay?: NativeRelayClientDescriptor;
  /** Reuse the App Runtime's authenticated Cloudflare client socket. */
  multiplex?: CloudflareNativeTerminalMultiplexRelay;
  webSocketUrl?: string;
  webSocketFactory?: (url: string) => WebSocket;
  /** Bounds one control-socket handshake; callers may retry with a new ticket. */
  connectTimeoutMs?: number;
  /** Optional local-daemon browser authentication frame sent before the Cloud ticket. */
  authenticationFrame?: unknown;
  onEvent?: (event: RemoteTerminalControllerEvent) => void;
}

export interface RemoteTerminalControlResult {
  target?: RemoteTerminalControllerTarget;
  lease_epoch?: number;
  operation_id?: string;
  outcome?: "applied" | "lease_rejected" | "attempt_superseded" | "rejected";
}

export interface RemoteTerminalUploadResult {
  target: RemoteTerminalControllerTarget;
  lease_epoch: number;
  operation_id: string;
  upload_id: string;
  outcome: "applied" | "attempt_superseded" | "rejected";
  path?: string;
  expires_at?: string;
  error?: string;
}

export type RemoteTerminalControllerEvent =
  | { type: "ready"; attachmentId: string }
  | { type: "controller_state"; state: RemoteTerminalControllerState }
  | { type: "lease"; lease: RemoteTerminalControllerLease }
  | { type: "result"; requestId: string; result: RemoteTerminalControlResult }
  | { type: "error"; requestId: string | null; reason: string }
  | { type: "closed"; reason?: string };

/** Writable terminal attachment. It never stores terminal output or tickets. */
export class RemoteTerminalController {
  private readonly options: RemoteTerminalControllerOptions;
  private socket: WebSocket | null = null;
  private relay: CloudflareNativeTerminalRelay | null = null;
  private multiplexStream: NativeTerminalMultiplexStream | null = null;
  private readonly listeners = new Set<(event: RemoteTerminalControllerEvent) => void>();
  private readyResolve: (() => void) | null = null;
  private readyReject: ((error: Error) => void) | null = null;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly uploadWaiters = new Map<string, {
    resolve: (result: RemoteTerminalUploadResult) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();

  constructor(options: RemoteTerminalControllerOptions) {
    this.options = options;
    if (options.onEvent) this.listeners.add(options.onEvent);
  }

  subscribe(listener: (event: RemoteTerminalControllerEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async connect(): Promise<void> {
    // Cloudflare multiplex owns the authenticated device socket. Do not open
    // the legacy per-Session control WebSocket first: that extra handshake
    // made observe -> control -> lease serial, and it also left two competing
    // control attachments for one Session. The multiplex stream carries the
    // typed control-ready/lease/input/resize/upload frames directly to the
    // Daemon source.
    if (this.options.relay && this.options.multiplex) {
      await this.connectMultiplex();
      return;
    }
    if (this.socket) return;
    const url = this.options.webSocketUrl
      ? new URL(this.options.webSocketUrl)
      : remoteTerminalUrl(
        this.options.cloudUrl,
        this.options.deviceId,
      );
    if (!this.options.webSocketUrl) url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const socket = (this.options.webSocketFactory ?? ((value: string) => new WebSocket(value)))(url.toString());
    this.socket = socket;
    const ready = new Promise<void>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    this.readyTimer = setTimeout(() => {
      this.readyTimer = null;
      const error = new OrniTransportError(
        "TIMEOUT",
        "Remote terminal control handshake stalled; retrying with a fresh ticket.",
        true,
      );
      this.readyReject?.(error);
      this.readyReject = null;
      if (socket.readyState < 2) socket.close(1000, "control handshake retry");
    }, Math.max(25, this.options.connectTimeoutMs ?? 10_000));
    socket.onopen = () => {
      if (this.options.authenticationFrame !== undefined) {
        socket.send(JSON.stringify(this.options.authenticationFrame));
      }
      socket.send(JSON.stringify({
        ticket: this.options.ticket,
        target: this.options.target,
        attachment_id: this.options.attachmentId,
      }));
    };
    socket.onmessage = (event) => {
      try {
        this.receive(String(event.data));
      } catch (cause) {
        const reason = cause instanceof Error ? cause.message : "invalid remote terminal control message";
        this.readyReject?.(new Error(reason));
        this.emit({ type: "error", requestId: null, reason });
        this.close();
      }
    };
    socket.onerror = () => {
      this.clearReadyTimer();
      this.readyReject?.(new Error("remote terminal control connection failed"));
      this.emit({ type: "error", requestId: null, reason: "remote terminal control connection failed" });
    };
    socket.onclose = (event) => {
      this.clearReadyTimer();
      this.socket = null;
      this.readyReject?.(new Error(event.reason || "remote terminal control closed"));
      for (const [operationId, waiter] of this.uploadWaiters) {
        clearTimeout(waiter.timer);
        waiter.reject(new Error(event.reason || "remote terminal control closed"));
        this.uploadWaiters.delete(operationId);
      }
      this.emit({ type: "closed", reason: event.reason || undefined });
    };
    await ready;
    if (this.options.relay) {
      const descriptor = this.options.relay;
      const streamId = `${this.options.target.session_id}:${this.options.target.process_attempt_id}:${this.options.target.generation}`;
      const relay = new CloudflareNativeTerminalRelay({
        relayUrl: descriptor.relayUrl,
        role: "client",
        token: descriptor.clientToken,
        epoch: descriptor.epoch,
        streamId,
        webSocketFactory: this.options.webSocketFactory,
        onMessage: (message) => {
          if (message.type === "frame") {
            try {
              this.receive(JSON.stringify(message.body));
            } catch (cause) {
              const reason = cause instanceof Error ? cause.message : "invalid relay control message";
              this.emit({ type: "error", requestId: null, reason });
              this.close();
            }
          } else if (message.type === "close") {
            this.emit({ type: "closed", reason: message.reason });
          }
        },
        connectTimeoutMs: this.options.connectTimeoutMs,
      });
      this.relay = relay;
      try {
        await relay.connect();
      } catch (error) {
        relay.close();
        this.relay = null;
        this.close();
        throw error;
      }
    }
  }

  private async connectMultiplex(): Promise<void> {
    if (this.multiplexStream) return;
    const descriptor = this.options.relay;
    const multiplex = this.options.multiplex;
    if (!descriptor || !multiplex) {
      throw new OrniTransportError("CONTRACT_INVALID", "Cloudflare multiplex relay configuration is incomplete.");
    }
    const ready = new Promise<void>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    this.readyTimer = setTimeout(() => {
      this.readyTimer = null;
      const error = new OrniTransportError(
        "TIMEOUT",
        "Remote terminal multiplex control handshake stalled; retrying with a fresh ticket.",
        true,
      );
      this.readyReject?.(error);
      this.readyReject = null;
    }, Math.max(25, this.options.connectTimeoutMs ?? 10_000));
    const streamId = `${this.options.target.session_id}:${this.options.target.process_attempt_id}:${this.options.target.generation}`;
    let stream: NativeTerminalMultiplexStream | null = null;
    try {
      stream = await multiplex.openStream(descriptor, streamId, (message) => {
        if (message.type === "frame") {
          try {
            this.receive(JSON.stringify(message.body));
          } catch (cause) {
            const reason = cause instanceof Error ? cause.message : "invalid relay control message";
            this.emit({ type: "error", requestId: null, reason });
            this.close();
          }
        } else if (message.type === "close") {
          this.readyReject?.(new Error(message.reason || "remote terminal multiplex stream closed"));
          this.emit({ type: "closed", reason: message.reason });
        }
      });
      this.multiplexStream = stream;
      stream.sendFrame({
        type: "control_ready",
        request_id: randomUuid(),
        target: this.options.target,
        attachment_id: this.options.attachmentId,
      });
      await ready;
    } catch (error) {
      this.clearReadyTimer();
      this.readyResolve = null;
      this.readyReject = null;
      if (stream && this.multiplexStream === stream) {
        this.multiplexStream = null;
        stream.close("control handshake failed");
      }
      throw error;
    }
  }

  close(): void {
    this.clearReadyTimer();
    this.socket?.close(1000, "controller closed");
    this.socket = null;
    this.relay?.close(1000, "controller closed");
    this.relay = null;
    this.multiplexStream?.close("controller closed");
    this.multiplexStream = null;
    for (const [operationId, waiter] of this.uploadWaiters) {
      clearTimeout(waiter.timer);
      waiter.reject(new Error("remote terminal control closed during image upload"));
      this.uploadWaiters.delete(operationId);
    }
  }

  lease(request: {
    operationId: string;
    action: "acquire" | "renew" | "release";
    force?: boolean;
    expectedRevision?: number;
    proof?: RemoteTerminalLeaseProof;
  }): void {
    this.send({
      type: "lease",
      request: {
        operation_id: request.operationId,
        action: request.action,
        target: this.options.target,
        attachment_id: this.options.attachmentId,
        expected_revision: request.expectedRevision ?? null,
        force: request.force === true,
        proof: request.proof ? wireProof(request.proof) : null,
      },
    }, this.multiplexStream ? "relay" : "legacy");
  }

  input(proof: RemoteTerminalLeaseProof, bytes: Uint8Array, operationId = newOperationId()): string {
    this.send({
      type: "input",
      input: { operation_id: operationId, lease: wireProof(proof), bytes: Array.from(bytes) },
    }, this.relay || this.multiplexStream ? "relay" : "legacy");
    return operationId;
  }

  resize(
    proof: RemoteTerminalLeaseProof,
    rows: number,
    columns: number,
    operationId = newOperationId(),
  ): string {
    this.send({
      type: "resize",
      resize: {
        operation_id: operationId,
        lease: wireProof(proof),
        rows,
        columns,
      },
    }, this.relay || this.multiplexStream ? "relay" : "legacy");
    return operationId;
  }

  /** Answer a Provider-owned terminal prompt using its opaque option id. */
  answerPrompt(
    proof: RemoteTerminalLeaseProof,
    promptId: string,
    optionId: string,
    operationId = newOperationId(),
  ): string {
    this.send({
      type: "answer_prompt",
      lease: wireProof(proof),
      answer: {
        operation_id: operationId,
        interaction_id: promptId,
        option_id: optionId,
      },
    }, this.multiplexStream ? "relay" : "legacy");
    return operationId;
  }

  /** Stage an image on the target Daemon and return its real local path. */
  async uploadImage(
    proof: RemoteTerminalLeaseProof,
    input: { uploadId?: string; fileName: string; mimeType: string; bytes: Uint8Array },
    onProgress?: (uploadedBytes: number, totalBytes: number) => void,
  ): Promise<RemoteTerminalUploadResult> {
    if (input.bytes.byteLength === 0 || input.bytes.byteLength > 10 * 1024 * 1024) {
      throw new OrniTransportError("INVALID_ARGUMENT", "Image size is outside the supported limit.");
    }
    const uploadId = input.uploadId ?? `upl_${randomUuid().replaceAll("-", "")}`;
    const total = input.bytes.byteLength;
    await this.sendUpload(proof, "upload_begin", {
      operation_id: newOperationId(),
      upload_id: uploadId,
      file_name: input.fileName,
      mime_type: input.mimeType,
      total_bytes: total,
    });
    // Relay control frames are JSON envelopes (the binary PTY path is kept
    // for raw output/input). A Uint8Array encoded as a JSON number array is
    // substantially larger than the raw upload chunk, so keep relay chunks
    // below the Worker's bounded control-frame limit.
    const chunkSize = this.relay || this.multiplexStream ? 16 * 1024 : 48 * 1024;
    let offset = 0;
    while (offset < total) {
      const bytes = input.bytes.slice(offset, Math.min(total, offset + chunkSize));
      await this.sendUpload(proof, "upload_chunk", {
        operation_id: newOperationId(),
        upload_id: uploadId,
        offset,
        bytes: Array.from(bytes),
      });
      offset += bytes.byteLength;
      onProgress?.(offset, total);
    }
    return this.sendUpload(proof, "upload_complete", {
      operation_id: newOperationId(),
      upload_id: uploadId,
    });
  }

  private sendUpload(
    proof: RemoteTerminalLeaseProof,
    type: "upload_begin" | "upload_chunk" | "upload_complete",
    value: Record<string, unknown>,
  ): Promise<RemoteTerminalUploadResult> {
    const operationId = String(value.operation_id);
    const field = type === "upload_begin" ? "begin" : type === "upload_chunk" ? "chunk" : "complete";
    const result = new Promise<RemoteTerminalUploadResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.uploadWaiters.delete(operationId);
        reject(new OrniTransportError("TIMEOUT", "Timed out while staging the image on the computer.", true));
      }, 30_000);
      this.uploadWaiters.set(operationId, { resolve, reject, timer });
    });
    this.send({ type, lease: wireProof(proof), [field]: value }, this.relay || this.multiplexStream ? "relay" : "legacy");
    return result;
  }

  private send(value: unknown, transport: "legacy" | "relay" = "legacy"): void {
    if (transport === "relay") {
      if (!this.relay && !this.multiplexStream) throw new OrniTransportError("UNAVAILABLE", "Cloudflare relay is not connected.", true);
      const command = value as Record<string, unknown>;
      const commandType = command.type;
      const type = commandType === "input" || commandType === "resize"
        ? "control"
        : commandType === "answer_prompt"
          ? "control"
        : commandType === "upload_begin" || commandType === "upload_chunk" || commandType === "upload_complete"
          ? "upload"
          : commandType === "lease" || commandType === "control_ready"
            ? commandType
          : null;
      if (!type) throw new OrniTransportError("INVALID_ARGUMENT", "Unsupported Cloudflare terminal command.");
      const requestId = randomUuid();
      const sendFrame = (body: Record<string, unknown>) => {
        if (this.multiplexStream) this.multiplexStream.sendFrame(body);
        else this.relay!.sendFrame(body);
      };
      if (type === "lease" || type === "control_ready") {
        // The Daemon source protocol treats both handshake/control-ready and
        // lease frames as request-scoped server messages.  The legacy socket
        // path used to hide this correlation inside the WebSocket lifecycle,
        // but the multiplex relay forwards the opaque frame directly and
        // therefore must carry the request id on leases as well.  Omitting it
        // makes the source reject the frame (`missing field request_id`) and
        // tears down an otherwise healthy terminal attachment immediately
        // after control_ready.
        sendFrame({
          ...command,
          request_id: requestId,
        });
        return;
      }
      if (type === "control") {
        const controlType = commandType as "input" | "resize" | "answer_prompt";
        const field = controlType === "answer_prompt" ? "answer" : controlType;
        const nested = command[field] as Record<string, unknown>;
        // The Rust device protocol uses an internally tagged command whose
        // payload remains under the variant name. Flattening the payload here
        // makes serde reject the frame because `lease`/`input` are expected
        // siblings inside `command`, not fields of the outer frame.
        sendFrame({
          type,
          request_id: requestId,
          command: {
            type: controlType,
            lease: command.lease ?? nested.lease,
            [field]: nested,
          },
        });
      } else {
        const uploadCommandType = commandType as "upload_begin" | "upload_chunk" | "upload_complete";
        const uploadType = uploadCommandType === "upload_begin" ? "begin" : uploadCommandType === "upload_chunk" ? "chunk" : "complete";
        const nested = command[uploadType];
        sendFrame({
          type,
          request_id: requestId,
          command: {
            type: uploadType,
            lease: command.lease,
            [uploadType]: nested,
          },
        });
      }
      return;
    }
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error("remote terminal control is not connected");
    }
    this.socket.send(JSON.stringify(value));
  }

  private receive(text: string): void {
    let value: Record<string, unknown>;
    try {
      value = JSON.parse(text) as Record<string, unknown>;
    } catch {
      this.emit({ type: "error", requestId: null, reason: "invalid remote terminal control message" });
      return;
    }
    switch (value.type) {
      case "snapshot":
      case "output":
      case "gap":
      case "terminal_prompt":
      case "terminal_prompt_resolved":
        // Cloudflare multiplexes observe and control attachments over one
        // target stream. Display frames are consumed by the observer; they
        // are not control-protocol errors and must not revoke the lease.
        return;
      case "control_ready": {
        const attachmentId = String(value.attachment_id ?? "");
        if (attachmentId !== this.options.attachmentId) {
          throw new Error("remote terminal returned a different control attachment");
        }
        this.clearReadyTimer();
        this.readyResolve?.();
        this.readyResolve = null;
        this.readyReject = null;
        this.emit({ type: "ready", attachmentId });
        return;
      }
      case "controller_state": {
        const state = parseControllerState(value.state);
        if (!sameTarget(state.target, this.options.target)) {
          throw new Error("remote terminal returned controller state for a different target");
        }
        this.emit({ type: "controller_state", state });
        return;
      }
      case "lease": {
        const lease = parseLease(value.lease);
        if (!sameTarget(lease.target, this.options.target)) {
          throw new Error("remote terminal returned a controller lease for a different target");
        }
        // A multiplex stream broadcasts lease responses so every attachment
        // can receive the accompanying read-only controller_state projection.
        // A lease proof owned by another attachment is not a protocol error;
        // it is the normal signal that this controller is no longer writable.
        if (lease.owner_attachment_id !== this.options.attachmentId) return;
        this.emit({ type: "lease", lease });
        return;
      }
      case "control_result":
        this.emit({ type: "result", requestId: String(value.request_id ?? ""), result: parseControlResult(value.result) });
        return;
      case "control_error":
        this.emit({ type: "error", requestId: String(value.request_id ?? ""), reason: String(value.reason ?? "control rejected") });
        return;
      case "upload_result": {
        const result = parseUploadResult(value.result);
        const waiter = this.uploadWaiters.get(result.operation_id);
        if (!waiter) return;
        clearTimeout(waiter.timer);
        this.uploadWaiters.delete(result.operation_id);
        if (result.outcome === "applied") waiter.resolve(result);
        else waiter.reject(new OrniTransportError(
          result.outcome === "attempt_superseded" ? "CONFLICT" : "UNAVAILABLE",
          result.error || "Image staging was rejected.",
          result.outcome === "attempt_superseded",
        ));
        return;
      }
      case "upload_error": {
        const reason = String(value.reason ?? "upload rejected");
        for (const [operationId, waiter] of this.uploadWaiters) {
          clearTimeout(waiter.timer);
          waiter.reject(new Error(reason));
          this.uploadWaiters.delete(operationId);
        }
        this.emit({ type: "error", requestId: String(value.request_id ?? ""), reason });
        return;
      }
      case "closed":
        this.emit({ type: "closed", reason: String((value.reason as string | undefined) ?? "") || undefined });
        return;
      default:
        this.emit({ type: "error", requestId: null, reason: "unknown remote terminal control message" });
    }
  }

  private emit(event: RemoteTerminalControllerEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  private clearReadyTimer(): void {
    if (this.readyTimer !== null) clearTimeout(this.readyTimer);
    this.readyTimer = null;
  }
}

function parseUploadResult(value: unknown): RemoteTerminalUploadResult {
  const row = record(value, "upload result");
  const target = record(row.target, "upload result.target");
  const outcome = row.outcome;
  if (outcome !== "applied" && outcome !== "attempt_superseded" && outcome !== "rejected") {
    throw new Error("remote terminal returned an invalid upload outcome");
  }
  return {
    target: {
      session_id: text(target.session_id, "upload result.target.session_id"),
      process_attempt_id: text(target.process_attempt_id, "upload result.target.process_attempt_id"),
      generation: positiveInteger(target.generation, "upload result.target.generation"),
    },
    lease_epoch: positiveInteger(row.lease_epoch, "upload result.lease_epoch"),
    operation_id: text(row.operation_id, "upload result.operation_id"),
    upload_id: text(row.upload_id, "upload result.upload_id"),
    outcome,
    ...(typeof row.path === "string" ? { path: row.path } : {}),
    ...(typeof row.expires_at === "string" ? { expires_at: timestamp(row.expires_at, "upload result.expires_at") } : {}),
    ...(typeof row.error === "string" ? { error: row.error } : {}),
  };
}

function parseControlResult(value: unknown): RemoteTerminalControlResult {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("remote terminal returned an invalid control result");
  }
  const row = value as Record<string, unknown>;
  const outcome = row.outcome;
  if (outcome !== undefined
    && outcome !== "applied"
    && outcome !== "lease_rejected"
    && outcome !== "attempt_superseded"
    && outcome !== "rejected") {
    throw new Error("remote terminal returned an invalid control outcome");
  }
  return {
    ...(typeof row.operation_id === "string" ? { operation_id: row.operation_id } : {}),
    ...(typeof row.lease_epoch === "number" ? { lease_epoch: row.lease_epoch } : {}),
    ...(outcome === undefined ? {} : { outcome }),
    ...(row.target && typeof row.target === "object" && !Array.isArray(row.target)
      ? { target: row.target as RemoteTerminalControllerTarget }
      : {}),
  };
}

function wireProof(proof: RemoteTerminalLeaseProof): RemoteTerminalLeaseProof {
  return {
    lease_id: proof.lease_id,
    lease_epoch: proof.lease_epoch,
    owner_attachment_id: proof.owner_attachment_id,
    target: {
      session_id: proof.target.session_id,
      process_attempt_id: proof.target.process_attempt_id,
      generation: proof.target.generation,
    },
  };
}

function remoteTerminalUrl(cloudUrl: string, deviceId: string): URL {
  const baseUrl = cloudUrl || globalThis.location?.origin;
  if (!baseUrl) throw new OrniTransportError("INVALID_ARGUMENT", "A Cloud origin is required for remote terminal control.");
  return new URL(
    `/v1/devices/${encodeURIComponent(deviceId)}/remote-terminal/control`,
    baseUrl,
  );
}

function parseLease(value: unknown): RemoteTerminalControllerLease {
  const row = record(value, "lease");
  const target = record(row.target, "lease.target");
  const status = row.status;
  if (status !== "offered" && status !== "active" && status !== "revoked"
    && status !== "expired" && status !== "superseded") {
    throw new Error("remote terminal returned an invalid controller lease status");
  }
  return {
    lease_id: text(row.lease_id, "lease.lease_id"),
    lease_epoch: positiveInteger(row.epoch ?? row.lease_epoch, "lease.epoch"),
    owner_attachment_id: text(row.owner_attachment_id, "lease.owner_attachment_id"),
    target: {
      session_id: text(target.session_id, "lease.target.session_id"),
      process_attempt_id: text(target.process_attempt_id, "lease.target.process_attempt_id"),
      generation: positiveInteger(target.generation, "lease.target.generation"),
    },
    status,
    renewed_at: timestamp(row.renewed_at, "lease.renewed_at"),
    expires_at: timestamp(row.expires_at, "lease.expires_at"),
    revision: positiveInteger(row.revision, "lease.revision"),
  };
}

function parseControllerState(value: unknown): RemoteTerminalControllerState {
  const row = record(value, "controller state");
  const target = record(row.target, "controller state.target");
  const owner = row.owner_attachment_id;
  const expires = row.expires_at;
  if (owner !== null && typeof owner !== "string") throw new Error("remote terminal returned an invalid controller owner");
  if (expires !== null && typeof expires !== "string") throw new Error("remote terminal returned an invalid controller expiry");
  return {
    target: {
      session_id: text(target.session_id, "controller state.target.session_id"),
      process_attempt_id: text(target.process_attempt_id, "controller state.target.process_attempt_id"),
      generation: positiveInteger(target.generation, "controller state.target.generation"),
    },
    owner_attachment_id: owner as string | null,
    epoch: positiveInteger(row.epoch, "controller state.epoch", true),
    revision: positiveInteger(row.revision, "controller state.revision", true),
    expires_at: expires as string | null,
  };
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`remote terminal returned an invalid ${label}`);
  }
  return value as Record<string, unknown>;
}

function text(value: unknown, label: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`remote terminal returned an invalid ${label}`);
  }
  return value;
}

function positiveInteger(value: unknown, label: string, allowZero = false): number {
  if (!Number.isSafeInteger(value) || Number(value) < (allowZero ? 0 : 1)) {
    throw new Error(`remote terminal returned an invalid ${label}`);
  }
  return Number(value);
}

function timestamp(value: unknown, label: string): string {
  const parsed = text(value, label);
  if (!Number.isFinite(Date.parse(parsed))) {
    throw new Error(`remote terminal returned an invalid ${label}`);
  }
  return parsed;
}

function sameTarget(
  left: RemoteTerminalControllerTarget,
  right: RemoteTerminalControllerTarget,
): boolean {
  return left.session_id === right.session_id
    && left.process_attempt_id === right.process_attempt_id
    && left.generation === right.generation;
}

function newOperationId(): string {
  return randomUuid();
}
